/** @jsxImportSource @emotion/react */
import {
  EuiButton,
  EuiButtonEmpty,
  EuiFieldText,
  EuiForm,
  EuiFormRow,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  htmlIdGenerator,
  EuiIcon,
  useEuiTheme,
} from "@inscopix/ideas-eui";
import { File as DrsFile } from "graphql/_Types";
import { useDrsFileUpdateDjango } from "hooks/useDrsFileUpdateDjango";
import { cloneDeep } from "lodash";
import { FormEvent, useState } from "react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { updateCacheFragment } from "utils/cache-fragments";
import { isDefined } from "utils/isDefined";
import {
  getFileBaseName,
  getFileExtensionFromFilename,
} from "utils/identifyFile";
import { useCallback } from "react";
import { css } from "@emotion/react";

const styles = {
  root: {
    width: 450,
  },
};
const MAX_NAME_LENGTH = 265;

export interface ModalRenameDrsFileProps {
  drsFile: Pick<DrsFile, "id" | "name">;
  onClose: () => void;
  onComplete?: (drsFileUpdated: Pick<DrsFile, "id" | "name">) => void;
  onError?: (error: Error) => void;
}

export const ModalRenameDrsFile = ({
  drsFile,
  onClose,
  onComplete,
  onError,
}: ModalRenameDrsFileProps) => {
  const formId = htmlIdGenerator()();
  const [fileName, setFileName] = useState(drsFile.name);
  const [loading, setLoading] = useState(false);
  const [extensionChanged, setExtensionChanged] = useState(false);
  const [extensionRemoved, setExtensionRemoved] = useState(false);
  const { euiTheme } = useEuiTheme();

  const { drsFileUpdate } = useDrsFileUpdateDjango();

  const nameRef = useCallback((input: HTMLInputElement | null) => {
    if (input === null) {
      // Component un-mounting, 🤸
      return;
    }
    const len = getFileBaseName(input.value).length;
    input.focus();
    input.setSelectionRange(0, len);
  }, []);

  const onNameChanged = (val: string) => {
    setFileName(val);
    const old_ext = getFileExtensionFromFilename(drsFile.name);
    const new_ext = getFileExtensionFromFilename(val);
    setExtensionChanged(old_ext !== new_ext);
    // Only consider extension removed if the original filename did not have an extension
    if (drsFile.name.indexOf(".") > 0) {
      setExtensionRemoved(new_ext.trim() === "" || val.indexOf(".") < 1);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (fileName === drsFile.name) {
      return onClose();
    }
    setLoading(true);

    try {
      const { id, name } = await drsFileUpdate(drsFile.id, {
        name: fileName.trim(),
      });

      updateCacheFragment({
        __typename: "File",
        id,
        update: (data) => {
          const newData = cloneDeep(data);
          newData.name = name;
          return newData;
        },
      });

      if (isDefined(onComplete)) {
        onComplete({ id, name });
      }
      addUtilityToastSuccess("Successfully renamed file");
    } catch (err) {
      if (isDefined(onError)) {
        onError(err as Error);
      }
      addUtilityToastFailure("Failed to rename file");
    }

    setLoading(false);

    onClose();
  };

  const invalidCharRegExp = /[^a-zA-Z0-9._-]/g;

  const nameError = (() => {
    if (fileName.trim().length === 0) {
      return "Please specify a name";
    }
    if (fileName.trim().length > MAX_NAME_LENGTH) {
      return "Character limit exceeded";
    }
    if (extensionRemoved) {
      return "You may not remove a file's extension.";
    }
    if (
      fileName.match(invalidCharRegExp) &&
      fileName.match(invalidCharRegExp)?.length != null
    ) {
      return "File name may only contain letters, numbers, periods, dashes, and underscores";
    }
  })();

  const isInvalid = nameError !== undefined;

  const extensionWarning =
    extensionChanged && !extensionRemoved ? (
      <span
        css={css`
          color: ${euiTheme.colors.warningText};
        `}
      >
        <EuiIcon
          type="alert"
          size="s"
          color="warning"
          css={css`
            margin-right: 4px;
            vertical-align: text-bottom;
          `}
        />
        Changing a file&apos;s extension may have unexpected side effects.
      </span>
    ) : undefined;
  return (
    <EuiModal onClose={onClose} style={styles.root}>
      <EuiModalHeader>
        <EuiModalHeaderTitle component="h3">Rename File</EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiForm
          id={formId}
          component="form"
          role="form"
          onSubmit={(e) => void handleSubmit(e)}
        >
          <EuiFormRow
            label="Name"
            isInvalid={isInvalid}
            error={nameError}
            helpText={extensionWarning}
          >
            <EuiFieldText
              title="Name"
              aria-label="Name"
              placeholder="Name"
              value={fileName}
              isInvalid={extensionRemoved}
              onChange={(e) => onNameChanged(e.target.value)}
              maxLength={MAX_NAME_LENGTH}
              inputRef={nameRef}
            />
          </EuiFormRow>
        </EuiForm>
      </EuiModalBody>
      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
        <EuiButton
          type="submit"
          form={formId}
          fill
          isLoading={loading}
          isDisabled={isInvalid}
        >
          Save
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
